'use client'

import React, { useState, useCallback, useEffect, useMemo, ReactNode } from 'react'
import PropTypes from 'prop-types'
import useEventListener from '../../../../hooks/useEventListener'
import { theme } from '../../../../theme'

type BreakpointType = keyof typeof theme.breakpoints
type ViewportType = number | null

const getViewPort = (): ViewportType => (typeof window !== 'undefined' && window.innerWidth) || null

const getScreen = (): BreakpointType | undefined => {
  const breakpoints = Object.values(theme.breakpoints)
  const viewport = getViewPort()

  if (!viewport) {
    return undefined
  }

  let screenClassIndex = breakpoints.findIndex((breakpoint) => viewport < breakpoint)

  if (screenClassIndex === -1) {
    screenClassIndex = breakpoints.length - 1
  }

  const screenClass = Object.keys(theme.breakpoints)[screenClassIndex] as BreakpointType


  return screenClass
}

type VisibleProps = {
  children: ReactNode
  breakpoints: BreakpointType | BreakpointType[]
}

function Visible({ children, breakpoints: propsBreakpoints = [] }: VisibleProps) {
  const [currentScreen, setCurrentScreen] = useState<BreakpointType | null>(null)

  const breakpoints = useMemo(() => Array.isArray(propsBreakpoints) ? propsBreakpoints : [propsBreakpoints], [propsBreakpoints])

  const setScreen = useCallback(() => {
    const newScreen = getScreen()
    setCurrentScreen((currentScreen) =>
      currentScreen === newScreen ? currentScreen : newScreen ?? null
    )
  }, [])

  useEffect(() => {
    setScreen()
  }, [setScreen])

  useEventListener('orientationchange', setScreen)
  useEventListener('resize', setScreen)

  const isVisible = useCallback(() => {
    return breakpoints.includes(currentScreen as BreakpointType)
  }, [breakpoints, currentScreen])

  return isVisible() ? <>{children}</> : null
}

const stringOrArray = PropTypes.oneOfType([PropTypes.string, PropTypes.array])

Visible.propTypes = {
  breakpoints: stringOrArray,
  children: PropTypes.node,
}

export default Visible
